import React, { useState, useEffect } from 'react';
import { Search, Globe, Activity, CheckCircle2, Save, Plus, Trash2, Edit3, X, ExternalLink } from 'lucide-react';
import api from '../api/client';
import { useToast } from '../contexts/ToastContext';

interface SEOSettings {
  title: string;
  description: string;
  h1: string;
  keywords: string[];
  metricaId: string;
}

export default function SEOManager() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SEOSettings>({
    title: '', description: '', h1: '', keywords: [], metricaId: ''
  });
  
  // Состояния для добавления/редактирования ключевика
  const [newKeyword, setNewKeyword] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editKeywordValue, setEditKeywordValue] = useState('');

  useEffect(() => {
    api.get('/seo-manager/settings')
      .then(res => {
        // Защита на случай, если старые данные пришли строкой
        const data = res.data;
        if (typeof data.keywords === 'string') {
          data.keywords = data.keywords.split(',').map((k: string) => k.trim());
        }
        setSettings(data);
      })
      .catch(() => showToast('Ошибка загрузки SEO настроек', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/seo-manager/settings', settings);
      showToast('SEO-настройки успешно сохранены!', 'success');
    } catch (err) {
      showToast('Ошибка при сохранении', 'error');
    } finally {
      setSaving(false);
    }
  };

  // --- Логика управления ключевыми словами ---
  const handleAddKeyword = () => {
    if (!newKeyword.trim()) return;
    if (settings.keywords.includes(newKeyword.trim())) {
      showToast('Такой ключевик уже есть', 'info');
      return;
    }
    setSettings({ ...settings, keywords: [...settings.keywords, newKeyword.trim()] });
    setNewKeyword('');
  };

  const handleRemoveKeyword = (index: number) => {
    const updated = settings.keywords.filter((_, i) => i !== index);
    setSettings({ ...settings, keywords: updated });
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditKeywordValue(settings.keywords[index]);
  };

  const saveEdit = () => {
    if (editingIndex === null || !editKeywordValue.trim()) return;
    const updated = [...settings.keywords];
    updated[editingIndex] = editKeywordValue.trim();
    setSettings({ ...settings, keywords: updated });
    setEditingIndex(null);
    setEditKeywordValue('');
  };

  const glassStyle = { background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 24, border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 8px 32px rgba(31,38,135,0.07)', marginBottom: 24 };
  const inputStyle = { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.9)', fontSize: 14, marginTop: 8 };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Загрузка SEO-центра...</div>;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', paddingBottom: 40 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <Search size={24} />
          </div>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>SEO-Центр управления</h1>
            <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>Управление видимостью и поведенческими факторами</p>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 12, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          <Save size={18} /> {saving ? 'Сохранение...' : 'Сохранить всё'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Блок 1: Мета-теги */}
        <div style={glassStyle}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><Globe size={20} color="#2575fc" /> Мета-теги (Главная)</h3>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Title (Заголовок страницы)</label>
          <input style={inputStyle} value={settings.title} onChange={e => setSettings({...settings, title: e.target.value})} />
          
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginTop: 16 }}>Description (Описание)</label>
          <textarea style={{ ...inputStyle, minHeight: 80 }} value={settings.description} onChange={e => setSettings({...settings, description: e.target.value})} />
          
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginTop: 16 }}>H1 (Главный заголовок)</label>
          <input style={inputStyle} value={settings.h1} onChange={e => setSettings({...settings, h1: e.target.value})} />
        </div>

        {/* Блок 2: ПФ и Метрика */}
        <div style={glassStyle}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><Activity size={20} color="#10b981" /> Поведенческие факторы</h3>
          <p style={{ fontSize: 13, color: '#64748b' }}>Настройки аналитики и инструменты для улучшения ПФ.</p>
          
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginTop: 16 }}>ID Яндекс.Метрики</label>
          <input style={inputStyle} value={settings.metricaId} onChange={e => setSettings({...settings, metricaId: e.target.value})} placeholder="Например, 109927177" />

          <div style={{ marginTop: 20, padding: 16, background: 'rgba(37,117,252,0.05)', borderRadius: 12, border: '1px solid rgba(37,117,252,0.1)' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Быстрые ссылки для SEO:</h4>
            <a href="https://webmaster.yandex.ru" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#2575fc', textDecoration: 'none', marginBottom: 8 }}><ExternalLink size={14} /> Яндекс.Вебмастер</a>
            <a href="https://metrika.yandex.ru" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#2575fc', textDecoration: 'none' }}><ExternalLink size={14} /> Яндекс.Метрика</a>
          </div>
        </div>
      </div>

      {/* Блок 3: Менеджер ключевых слов */}
      <div style={glassStyle}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><TagIcon /> Ключевые слова (Keywords)</h3>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Управляйте семантическим ядром. Эти слова используются в мета-тегах и помогают роботам понять тематику сайта.</p>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          {settings.keywords.map((kw, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(37,117,252,0.08)', border: '1px solid rgba(37,117,252,0.2)', padding: '8px 14px', borderRadius: 20 }}>
              {editingIndex === idx ? (
                <>
                  <input value={editKeywordValue} onChange={e => setEditKeywordValue(e.target.value)} autoFocus style={{ border: 'none', background: 'transparent', outline: 'none', width: 120, fontSize: 14, color: '#1a1a2e' }} />
                  <button onClick={saveEdit} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#10b981', padding: 0 }}><CheckCircle2 size={16} /></button>
                  <button onClick={() => setEditingIndex(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', padding: 0 }}><X size={16} /></button>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 14, color: '#1a1a2e', fontWeight: 500 }}>{kw}</span>
                  <button onClick={() => startEdit(idx)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 0 }}><Edit3 size={14} /></button>
                  <button onClick={() => handleRemoveKeyword(idx)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', padding: 0 }}><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
          {settings.keywords.length === 0 && <span style={{ color: '#94a3b8', fontSize: 14 }}>Ключевые слова не добавлены</span>}
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <input 
            style={{ ...inputStyle, marginTop: 0 }} 
            placeholder="Добавить новый ключевик (например: автоматизация HR)" 
            value={newKeyword} 
            onChange={e => setNewKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddKeyword()}
          />
          <button onClick={handleAddKeyword} style={{ background: '#2575fc', color: '#fff', border: 'none', padding: '0 24px', borderRadius: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={18} /> Добавить
          </button>
        </div>
      </div>

      
      {/* Блок Предпросмотра (Сниппет) */}
      <div style={glassStyle}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
          <Search size={20} color="#2575fc" /> Предпросмотр в поиске (Сниппет)
        </h3>
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #e2e8f0', maxWidth: 600, fontFamily: 'Arial, sans-serif' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#e2e8f0' }}></div>
            <span style={{ fontSize: 12, color: '#202124' }}>hrlama.ru</span>
          </div>
          <div style={{ fontSize: 18, color: '#1a0dab', fontWeight: 500, marginBottom: 4, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {settings.title || 'Title не задан'}
          </div>
          <div style={{ fontSize: 13, color: '#4d5156', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {settings.description || 'Description не задан. Добавьте описание, чтобы повысить CTR в поиске.'}
          </div>
        </div>
      </div>

      {/* Блок 4: Технический чек-лист */}
      <div style={glassStyle}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><CheckCircle2 size={20} color="#f59e0b" /> Технический чек-лист</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div style={{ padding: 16, background: 'rgba(16,185,129,0.1)', borderRadius: 12, color: '#10b981', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={16} /> robots.txt настроен
          </div>
          <div style={{ padding: 16, background: 'rgba(16,185,129,0.1)', borderRadius: 12, color: '#10b981', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={16} /> sitemap.xml отдается
          </div>
          <div style={{ padding: 16, background: 'rgba(16,185,129,0.1)', borderRadius: 12, color: '#10b981', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={16} /> JSON-LD (Schema) внедрена
          </div>
        </div>
      </div>
    </div>
  );
}

// Вспомогательный компонент иконки (чтобы не импортировать лишнего)
function TagIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#f59e0b' }}><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/></svg>;
}
